import React from 'react';
import axios from 'helpers/axios';
import ReCAPTCHA, { captchaSiteKey } from 'helpers/captcha';
import _ from 'lodash';

import { Container, Title, Label, TextInput, Button, Radio, Form } from 'ui/components/styled';
import { Logo } from 'ui/components/shared';
import { AuthLayout } from 'ui/components/layouts';
import { useUser, useToasts, useQuery, useLayout } from 'hooks';
import { BusinessType, BusinessTypes } from 'models';
import { ValidationPattern } from 'helpers/form';
import useMediaQuery from 'hooks/useMediaQuery';
import device from 'ui/theme/device';

export const SignUp = ({ _captcha = null }) => {
  const [state, setState] = React.useState({
    name: {
      first: '',
      last: '',
    },
    username: '',
    email: '',
    password: '',
    phone: '',
    data: {
      businessType: BusinessType.EndCustomer,
      feedback: '',
    },
  });
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [disabledFields, setDisabledFields] = React.useState([]);
  const [captcha, setCaptcha] = React.useState(_captcha);
  const [query] = useQuery();
  const { setLayoutLoading } = useLayout();
  const { isLoggedIn, saveUser, redirectTo } = useUser();
  const { errorToast } = useToasts();

  React.useEffect(async () => {
    try {
      if (query?.mid) {
        const response = await axios.get(`/companies/invitation/${query.mid}`);
        if (response.status === 200) {
          const { invitation } = response.data;
          setState((state) => ({ ...state, email: invitation.user.email }));
          setDisabledFields([...disabledFields, 'email']);
        }
      }
      if (query?.invid) {
        const response = await axios.get(`/uext/invitation/${query.invid}`);
        if (response.status === 200) {
          const { email } = response.data;
          setState((state) => ({ ...state, email }));
          setDisabledFields([...disabledFields, 'email']);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    _.set(state, name, value);
    setState({ ...state });
  };

  const handleCaptchaChange = (e) => {
    setCaptcha(e);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (state.password !== confirmPassword) {
      errorToast('Passwords does not match!');
      return;
    } else if (!captcha) {
      errorToast('captcha is required!');
      return;
    }
    try {
      setLayoutLoading(true);
      const params = query?.mid ? `?mid=${query.mid}` : query?.invid ? `?invid=${query.invid}` : '';
      const response = await axios.post(`/uext/auth/signup${params}`, {
        ...state,
        username: state.email,
      });
      if (response.status === 200) {
        saveUser(response.data, false);
        redirectTo('/phone/validate');
        return;
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Sign Up' });
    }
    setLayoutLoading(false);
  };
  const isLarge = useMediaQuery(device.large);

  return isLarge ? (
    <AuthLayout show={!isLoggedIn}>
      <Container width="940px" height="auto">
        <Logo mb="m" link="/sign-in" />
        <Title mb="m">Register for Process Watch</Title>
        <Form onSubmit={handleSubmit}>
          <Container flexDirection="row" mb="xl">
            <Container mr="xxl">
              <Label htmlFor="firstname" mb="xs">
                First name
              </Label>
              <TextInput
                id="firstname"
                name="name.first"
                placeholder="First name"
                value={state.name.first}
                onChange={handleChange}
                required
                autoFocus
              />
            </Container>
            <Container>
              <Label htmlFor="lastname" mb="xs">
                Last name
              </Label>
              <TextInput
                id="lastname"
                name="name.last"
                placeholder="Last name"
                value={state.name.last}
                onChange={handleChange}
                required
              />
            </Container>
          </Container>
          <Container flexDirection="row" mb="xl">
            <Container mr="xxl">
              <Label htmlFor="email" mb="xs">
                Email address
              </Label>
              <TextInput
                id="email"
                name="email"
                type="email"
                placeholder="Email"
                value={state.email}
                disabled={disabledFields.includes('email')}
                onChange={handleChange}
                required
              />
            </Container>
            <Container>
              <Label htmlFor="phone" mb="xs">
                Phone
              </Label>
              <TextInput
                id="phone"
                name="phone"
                type="tel"
                pattern={ValidationPattern.phone}
                placeholder="Phone"
                value={state.phone}
                onChange={handleChange}
                required
              />
            </Container>
          </Container>
          <Container flexDirection="row" mb="xl">
            <Container mr="xxl">
              <Label htmlFor="pwd" mb="xs">
                Password
              </Label>
              <TextInput
                id="pwd"
                name="password"
                type="password"
                minLength="10"
                placeholder="Password"
                value={state.password}
                onChange={handleChange}
                required
              />
            </Container>
            <Container>
              <Label htmlFor="confirm-pwd" mb="xs">
                Confirm password
              </Label>
              <TextInput
                id="confirm-pwd"
                type="password"
                minLength="10"
                placeholder="Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </Container>
          </Container>
          <Container flexDirection="row" mb="xl">
            <Container mr="xxl">
              <Label htmlFor="businessType" mb="xs">
                Business Type
              </Label>
              <Container flexDirection="row" alignItems="center">
                {BusinessTypes.map((type, index) => (
                  <Radio
                    key={index}
                    name="data.businessType"
                    value={type.value}
                    checked={state.data.businessType === type.value}
                    onChange={handleChange}
                    required
                  >
                    {type.label}
                  </Radio>
                ))}
              </Container>
            </Container>
            <Container>
              <Label htmlFor="feedback" mb="xs">
                How did you hear about Process Watch?
              </Label>
              <TextInput
                id="feedback"
                name="data.feedback"
                placeholder="How did you hear about Process Watch?"
                data-testid="feedback"
                value={state.data.feedback}
                onChange={handleChange}
                required
              />
            </Container>
          </Container>
          <Container flexDirection="row" mb="xl">
            <Container mr="xxl">
              <ReCAPTCHA sitekey={captchaSiteKey} onChange={handleCaptchaChange} />
            </Container>
            <Container></Container>
          </Container>
          <Button type="submit" width="50%" my="m">
            Sign up
          </Button>
        </Form>
      </Container>
    </AuthLayout>
  ) : (
    <AuthLayout show={!isLoggedIn}>
      <Container width="80%" maxWidth="480px" height="auto" pt="2rem" pb="2rem">
        <Logo mb="m" link="/sign-in" />
        <Title mb="m">Register for Process Watch</Title>
        <Form onSubmit={handleSubmit}>
          <Container mb="10px">
            <Label mb="5px" htmlFor="firstname">
              First name
            </Label>
            <TextInput
              id="firstname"
              name="name.first"
              placeholder="First name"
              value={state.name.first}
              onChange={handleChange}
              required
              autoFocus
            />
          </Container>
          <Container mb="10px">
            <Label mb="5px" htmlFor="lastname">
              Last name
            </Label>
            <TextInput
              id="lastname"
              name="name.last"
              placeholder="Last name"
              value={state.name.last}
              onChange={handleChange}
              required
            />
          </Container>
          <Container mb="10px">
            <Label mb="5px" htmlFor="email">
              Email address
            </Label>
            <TextInput
              id="email"
              name="email"
              type="email"
              placeholder="Email"
              value={state.email}
              disabled={disabledFields.includes('email')}
              onChange={handleChange}
              required
            />
          </Container>
          <Container mb="10px">
            <Label mb="5px" htmlFor="phone">
              Phone
            </Label>
            <TextInput
              id="phone"
              name="phone"
              type="tel"
              pattern={ValidationPattern.phone}
              placeholder="Phone"
              value={state.phone}
              onChange={handleChange}
              required
            />
          </Container>
          <Container mb="10px">
            <Label mb="5px" htmlFor="pwd">
              Password
            </Label>
            <TextInput
              id="pwd"
              name="password"
              type="password"
              minLength="10"
              placeholder="Password"
              value={state.password}
              onChange={handleChange}
              required
            />
          </Container>
          <Container mb="10px">
            <Label mb="5px" htmlFor="confirm-pwd">
              Confirm password
            </Label>
            <TextInput
              id="confirm-pwd"
              type="password"
              minLength="10"
              placeholder="Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </Container>
          <Container mb="10px">
            <Label mb="5px" htmlFor="businessType">
              Business Type
            </Label>
            <Container flexDirection="row" alignItems="center">
              {BusinessTypes.map((type, index) => (
                <Radio
                  key={index}
                  name="data.businessType"
                  value={type.value}
                  checked={state.data.businessType === type.value}
                  onChange={handleChange}
                  required
                >
                  {type.label}
                </Radio>
              ))}
            </Container>
          </Container>
          <Container mb="10px">
            <Label mb="5px" htmlFor="feedback">
              How did you hear about Process Watch?
            </Label>
            <TextInput
              id="feedback"
              name="data.feedback"
              placeholder="How did you hear about Process Watch?"
              data-testid="feedback"
              value={state.data.feedback}
              onChange={handleChange}
              required
            />
          </Container>
          <Container mb="10px" alignItems="center">
            <ReCAPTCHA width="100%" sitekey={captchaSiteKey} onChange={handleCaptchaChange} />
          </Container>
          <Button type="submit" width="50%">
            Sign up
          </Button>
        </Form>
      </Container>
    </AuthLayout>
  );
};
